import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  CreateOrJoinDineInSessionPayload,
  AddDineInItemPayload,
  SubmitDineInRoundPayload,
  DecideDineInItemPayload,
  DecideDineInRoundPayload,
  RawDineInSession,
} from "../../interfaces/DineInObject";
import { postRequest, getRequest, putRequest } from "../http/httpRequest";
import {
  unwrapEnvelope,
  coerceSingle,
  coerceList,
  ApiEnvelopeError,
} from "../helpers/ApiEnvelopeHelper";

// ─── Dine In ──────────────────────────────────────────────────────────────
// Every endpoint here lives under /api/v1/dining and returns the same session
// envelope (see RawDineInSession) — the whole session comes back after any
// mutation, which is why no call here needs to merge a partial response.
//
// Paths, verbs and payloads reshaped 2026-08-11 against the Verticals API
// spec: resources pluralised, ids moved from bodies into paths, and the round
// update switched POST -> PUT. The ARGUMENT shapes below deliberately did not
// change, so no thunk, slice or component was touched — the split between
// "what callers pass" and "what goes on the wire" happens here and nowhere
// else. See ServiceEndpoints for the full route list.
//
// Order below mirrors the session lifecycle: open/join a table, add items to
// the current round, close a round out, re-read one session — then
// getSessionsByBusiness, the odd one out: the business/kitchen side,
// returning a LIST of those envelopes rather than one.

/** Backend error codes for the dining endpoints. */
export const DINE_IN_ERROR_CODES = {
  SESSION_NOT_FOUND: "10001",
  ROUND_NOT_FOUND: "10002",
  PRODUCT_NOT_FOUND: "10003",
  PRODUCT_NOT_IN_BUSINESS: "10004",
  NOT_A_SESSION_MEMBER: "10005",
  INVALID_OR_MISSING_SESSION_CODE: "10006",
  MISSING_BUSINESS_OR_TABLE: "10007",
  SESSION_CLOSED: "10019",
  PENDING_ITEMS: "10020",
  NOTHING_TO_BILL: "10021",
} as const;

export const DINE_IN_FRIENDLY_MESSAGES: Record<string, string> = {
  [DINE_IN_ERROR_CODES.SESSION_NOT_FOUND]:
    "This table's session isn't active anymore. Try scanning the QR code again.",
  [DINE_IN_ERROR_CODES.ROUND_NOT_FOUND]: "That round isn't available anymore.",
  [DINE_IN_ERROR_CODES.PRODUCT_NOT_FOUND]: "That item isn't available anymore.",
  [DINE_IN_ERROR_CODES.PRODUCT_NOT_IN_BUSINESS]:
    "That item isn't on this table's menu.",
  [DINE_IN_ERROR_CODES.NOT_A_SESSION_MEMBER]:
    "You're not part of this table's session anymore. Rejoin with the table code.",
  [DINE_IN_ERROR_CODES.INVALID_OR_MISSING_SESSION_CODE]:
    "That code doesn't look right. Double-check with the table.",
  [DINE_IN_ERROR_CODES.MISSING_BUSINESS_OR_TABLE]:
    "This table link looks broken. Try scanning the QR code again.",
  [DINE_IN_ERROR_CODES.SESSION_CLOSED]: "This table has already been closed.",
  [DINE_IN_ERROR_CODES.PENDING_ITEMS]:
    "Some items are still waiting for the kitchen. The bill can be generated once they're decided.",
  [DINE_IN_ERROR_CODES.NOTHING_TO_BILL]: "There's nothing to bill on this table yet.",
};

const KNOWN_DINE_IN_ERROR_CODES: ReadonlySet<string> = new Set(
  Object.values(DINE_IN_ERROR_CODES)
);

/** True only for the documented codes above — false for unwrapEnvelope's
 *  synthetic `HTTP_<status>` fallback (a plain HTTP failure with no backend
 *  errorCode) or any other code we don't have copy for. Callers use this to
 *  decide whether a code is safe to pass through raw (the slice branches on
 *  it) or needs to go through toFriendlyDineInError instead. */
export function isKnownDineInErrorCode(code: string): boolean {
  return KNOWN_DINE_IN_ERROR_CODES.has(code);
}

/**
 * Maps a raw dine-in error code to display copy. Passes anything else (e.g.
 * an already-friendly message built by toFriendlyDineInError below) through
 * unchanged, so callers can run any string through this safely — as long as
 * only known codes or already-friendly text ever reach here (see
 * isKnownDineInErrorCode; DineInThunk.ts relies on that invariant).
 */
export function friendlyDineInMessage(codeOrMessage: string): string {
  return DINE_IN_FRIENDLY_MESSAGES[codeOrMessage] ?? codeOrMessage;
}

/**
 * Converts any error thrown by this service into a display string. Never
 * surfaces a raw HTTP status or error code to the user — repo convention
 * (see ApiErrorHelper.ts) is to always translate to human copy.
 */
export function toFriendlyDineInError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    // Known codes get curated copy. Anything else — chiefly unwrapEnvelope's
    // synthetic HTTP_<status> code for a plain HTTP failure with no backend
    // errorCode — falls back to error.message, which unwrapEnvelope already
    // defaults to friendly copy ("Something went wrong. Please try again.").
    // Never echo the raw code/status itself.
    return isKnownDineInErrorCode(error.errorCode)
      ? friendlyDineInMessage(error.errorCode)
      : error.message;
  }
  // The request never got a response at all (offline, DNS, CORS, etc.) —
  // error.message here is a raw browser/JS string (e.g. "Failed to fetch"),
  // not something to show a user. Same copy as ApiErrorHelper.fetchOrThrowFriendly.
  return "Couldn't reach the server. Please check your connection and try again.";
}

/**
 * POST /api/v1/dining/dine-in-sessions. sessionCode is null on the very first
 * call for a table; the backend creates a session and returns it directly.
 * If a session already exists, a null or wrong code throws
 * ApiEnvelopeError(INVALID_OR_MISSING_SESSION_CODE) instead of joining.
 */
async function openTable(
  payload: CreateOrJoinDineInSessionPayload
): Promise<RawDineInSession> {
  const response = await postRequest(ServiceEndpoints.DINE_IN_SESSIONS, payload, true);
  const contents = await unwrapEnvelope<unknown>(response);
  return coerceSingle(contents) as unknown as RawDineInSession;
}

/**
 * POST /api/v1/dining/dine-in-sessions/{sessionId}/items — adds one or more
 * items to the session's current round in a single call.
 *
 * dineInId is pulled OUT of the caller's payload and into the path; only
 * `items` is sent as the body. Callers keep passing both together (see
 * AddDineInItemPayload) so nothing upstream had to change when the endpoint
 * moved the id.
 */
async function addItems({
  dineInId,
  items,
}: AddDineInItemPayload): Promise<RawDineInSession> {
  const response = await postRequest(
    `${ServiceEndpoints.DINE_IN_SESSIONS}/${dineInId}/items`,
    { items },
    true
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return coerceSingle(contents) as unknown as RawDineInSession;
}

/**
 * PUT /api/v1/dining/dine-in-rounds/{roundId} — closes out (or otherwise
 * updates the status of) a round. This is the diner pressing "Order".
 *
 * Note this is the round's own status, NOT the kitchen's verdict on it: the
 * kitchen decides a round through .../decision, which is a different route
 * and isn't wired yet.
 */
async function submitRound({
  roundId,
  status,
}: SubmitDineInRoundPayload): Promise<RawDineInSession> {
  const response = await putRequest(
    `${ServiceEndpoints.DINE_IN_ROUNDS}/${roundId}`,
    { status },
    true
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return coerceSingle(contents) as unknown as RawDineInSession;
}

/**
 * GET /api/v1/dining/dine-in-sessions/{sessionId} — read-only snapshot of the
 * session's current state (same envelope shape as the calls above), with no
 * session-code exchange and no join/create side effects. Exists purely so a
 * DINE_IN_* live-update push (which carries only {sessionId, type}, no delta)
 * can silently pull the latest state — see DineInThunk.refreshDineInSession.
 */
async function getSessionSnapshot(sessionId: string): Promise<RawDineInSession> {
  const response = await getRequest(
    `${ServiceEndpoints.DINE_IN_SESSIONS}/${sessionId}`,
    true
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return coerceSingle(contents) as unknown as RawDineInSession;
}

/**
 * GET /api/v1/dining/businesses/{businessId}/dine-in-sessions — every live
 * dine-in session at one business. This is the business/kitchen side of
 * dine-in: the diner's endpoints above are all scoped to a single table they
 * hold a session code for, whereas this one answers "what is happening across
 * the whole floor right now".
 *
 * Unwrapped with coerceList, NOT coerceSingle: `contents` here is a genuine
 * collection (one entry per table), so coerceSingle would quietly return only
 * the first table and every other one would vanish. Each element is the same
 * RawDineInSession envelope the other calls return, which is why the caller
 * can reuse convertRawDineInSession unchanged.
 *
 * Scope confirmed with product (2026-08-09): the server returns only LIVE
 * sessions, so there is deliberately no client-side status filter here — a
 * table present in this response is occupied, and any table absent from it is
 * free. If that ever changes, filter in the board builder rather than here;
 * this function should keep returning exactly what the server said.
 */
async function getSessionsByBusiness(businessId: string): Promise<RawDineInSession[]> {
  const response = await getRequest(
    `${ServiceEndpoints.DINE_IN_BUSINESSES}/${businessId}/dine-in-sessions`,
    true
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return coerceList(contents) as unknown as RawDineInSession[];
}

/**
 * PUT /api/v1/dining/dine-in-items/{roundItemId}/decision — the kitchen
 * accepting or rejecting ONE item.
 *
 * Distinct from submitRound above, which is the DINER committing a round.
 * Same envelope back as everything else, so the caller gets the whole session
 * and never has to merge a partial update.
 *
 * `note` is omitted from the body entirely when empty rather than sent as "",
 * so an accepted item doesn't end up with a blank statusNote that later reads
 * as "a reason was given and it was nothing".
 */
async function decideItem({
  roundItemId,
  status,
  note,
}: DecideDineInItemPayload): Promise<RawDineInSession> {
  const response = await putRequest(
    `${ServiceEndpoints.DINE_IN_ITEMS}/${roundItemId}/decision`,
    note ? { status, note } : { status },
    true
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return coerceSingle(contents) as unknown as RawDineInSession;
}

/**
 * PUT /api/v1/dining/dine-in-rounds/{roundId}/decision — the owner or a
 * collaborator deciding a whole round at once.
 *
 * Careful not to confuse this with submitRound, which PUTs the same resource
 * WITHOUT /decision and is the diner placing the round. Same id, adjacent
 * paths, opposite sides of the counter.
 */
async function decideRound({
  roundId,
  status,
  note,
}: DecideDineInRoundPayload): Promise<RawDineInSession> {
  const response = await putRequest(
    `${ServiceEndpoints.DINE_IN_ROUNDS}/${roundId}/decision`,
    note ? { status, note } : { status },
    true
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return coerceSingle(contents) as unknown as RawDineInSession;
}

/**
 * POST /api/v1/dining/dine-in-sessions/{sessionId}/bill — closes the table and
 * returns the resulting order (with `bill`). No body. Idempotent server-side:
 * calling it again returns the same bill. Unlike every other call here the
 * envelope carries an OrderDto, not a session, so the caller converts it with
 * the order converter.
 */
async function generateBill(sessionId: string): Promise<unknown> {
  const response = await postRequest(
    `${ServiceEndpoints.DINE_IN_SESSIONS}/${sessionId}/bill`,
    {},
    true
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return coerceSingle(contents);
}

const DineInApiService = {
  openTable,
  generateBill,
  addItems,
  submitRound,
  decideItem,
  decideRound,
  getSessionSnapshot,
  getSessionsByBusiness,
  toFriendlyDineInError,
  friendlyDineInMessage,
  isKnownDineInErrorCode,
};

export default DineInApiService;
